import { getFormProps, useForm, type FieldMetadata } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { format } from 'date-fns/fp';
import { useMemo } from 'react';
import { z } from 'zod';

import { CheckboxField } from '~/components/forms/checkbox-field';
import { TextField } from '~/components/forms/text-field';
import { Button } from '~/components/ui/button';
import { Modal } from '~/components/ui/modal';
import { type EventRegistrationHouseholdMember } from '~/domain/households/queries/get-household-members-for-event-registration.server';
import { useHouseholdWaiverContext } from '~/hooks/use-household-waiver-context';

import { WaiverModalContent } from './waiver-modal-content';

const requiredBoolean = () =>
  z.coerce.boolean().refine((value) => value === true, { message: 'Required' });

const createSchema = (
  hasHouseholdContext: boolean,
  selectedChildIds: string[],
  isAuthenticatedAdultSelected: boolean,
) => {
  const baseSchema = { fullName: z.string() };

  if (!hasHouseholdContext) {
    return z.object({
      ...baseSchema,
      accepted: requiredBoolean(),
    });
  }

  const householdSchema: Record<string, z.ZodTypeAny> = { ...baseSchema };

  if (isAuthenticatedAdultSelected) {
    householdSchema.acknowledgedForSelf = requiredBoolean();
  }

  selectedChildIds.forEach((childId) => {
    householdSchema[`acknowledgedForChild_${childId}`] = requiredBoolean();
  });

  return z.object(householdSchema);
};

const formatDate = format("yyyy/MM/dd 'at' HH:mm aa");

interface WaiverModalProps {
  open: boolean;
  description: React.ReactNode;
  waiverHtml: string;
  acceptedAt?: string | null;
  acceptedBy?: string | null;
  acceptedTitle?: string;
  fullName?: string;
  onClose: () => void;
  onAccept: (fullName: string, memberIds?: string[]) => void;
  householdMembers?: EventRegistrationHouseholdMember[];
  selectedMemberIds?: string[];
  authenticatedMemberId?: string | null;
  /** Name of the registrant (used for manual registration of minors) */
  registrantName?: string;
  /** Whether this waiver is for a minor (manual registration) */
  isForMinor?: boolean;
}

export function WaiverModal({
  open,
  description,
  waiverHtml,
  acceptedAt,
  acceptedBy,
  acceptedTitle,
  onClose,
  onAccept,
  householdMembers,
  selectedMemberIds = [],
  authenticatedMemberId,
  registrantName,
  isForMinor = false,
}: WaiverModalProps) {
  const accepted = Boolean(acceptedAt);

  const {
    hasHouseholdContext,
    selectedChildren,
    authenticatedAdult,
    isAuthenticatedAdultSelected,
    hasOtherSelectedAdults,
    selectedChildIds,
  } = useHouseholdWaiverContext({
    householdMembers,
    selectedMemberIds,
    authenticatedMemberId,
  });

  const schema = useMemo(
    () => createSchema(hasHouseholdContext, selectedChildIds, isAuthenticatedAdultSelected),
    [hasHouseholdContext, selectedChildIds, isAuthenticatedAdultSelected],
  );

  const defaultValues = useMemo(() => {
    const base: Record<string, string | boolean> = {
      fullName: acceptedBy && acceptedAt ? `${acceptedBy} (${formatDate(acceptedAt)})` : '',
    };

    if (!hasHouseholdContext) {
      base.accepted = accepted;
    } else {
      if (isAuthenticatedAdultSelected) {
        base.acknowledgedForSelf = accepted;
      }
      selectedChildren.forEach((child) => {
        base[`acknowledgedForChild_${child.id}`] = accepted;
      });
    }

    return base;
  }, [
    acceptedBy,
    acceptedAt,
    accepted,
    hasHouseholdContext,
    selectedChildren,
    isAuthenticatedAdultSelected,
  ]);

  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    constraint: getZodConstraint(schema),
    onValidate: ({ formData }) => parseWithZod(formData, { schema }),
    defaultValue: defaultValues,
    onSubmit: (event) => {
      event.preventDefault();

      const formData = new FormData(event.currentTarget);
      const submission = parseWithZod(formData, { schema });

      if (submission.status === 'success') {
        const { fullName } = submission.value;
        const memberIds = hasHouseholdContext ? selectedMemberIds : undefined;
        onAccept(fullName, memberIds);
      }
    },
  });

  return (
    <Modal
      title={accepted ? acceptedTitle : 'Review & Sign Waiver'}
      open={open}
      onClose={onClose}
      dialogContentProps={{
        className: 'overflow-hidden',
      }}
    >
      {description}
      <WaiverModalContent waiverHtml={waiverHtml}>
        <form {...getFormProps(form)} className="mt-6 p-4">
          <TextField field={fields.fullName} label="Enter Your Name" disabled={accepted} />
          {!hasHouseholdContext ? (
            <CheckboxField
              field={fields.accepted as FieldMetadata<boolean>}
              label={
                isForMinor
                  ? `I confirm I am a parent/guardian for ${registrantName || 'this minor'} and agree to this waiver on their behalf.`
                  : 'I confirm that I am at least 18 years old and that I have read and agree to this waiver.'
              }
              checkboxProps={{ disabled: accepted }}
              labelProps={{ className: 'text-body2 font-light' }}
            />
          ) : null}

          {hasHouseholdContext && !accepted ? (
            <div className="mt-4 space-y-3">
              {isAuthenticatedAdultSelected && authenticatedAdult ? (
                <CheckboxField
                  field={fields.acknowledgedForSelf as FieldMetadata<boolean>}
                  label={`I confirm that I, ${authenticatedAdult.firstName} ${authenticatedAdult.lastName}, am at least 18 years old and that I have read and agree to this waiver on my own behalf.`}
                  checkboxProps={{ disabled: accepted }}
                  labelProps={{ className: 'text-body2 font-light' }}
                />
              ) : null}

              {selectedChildren.map((child) => {
                const childName =
                  [child.firstName, child.lastName].filter(Boolean).join(' ') || 'this child';
                return (
                  <CheckboxField
                    key={child.id}
                    field={fields[`acknowledgedForChild_${child.id}`] as FieldMetadata<boolean>}
                    label={`I confirm I am a parent/guardian for ${childName} and agree to this waiver on their behalf.`}
                    checkboxProps={{ disabled: accepted }}
                    labelProps={{ className: 'text-body2 font-light' }}
                  />
                );
              })}
            </div>
          ) : null}

          {hasHouseholdContext && hasOtherSelectedAdults && !accepted ? (
            <p className="mt-8 text-sm font-bold">
              Any other adult household members who are being registered will need to sign their
              waiver when they arrive at the event and check in.
            </p>
          ) : null}

          {!accepted ? (
            <div className="mt-4 flex justify-center">
              <Button disabled={!form.dirty} type="submit" color="primary">
                Submit
              </Button>
            </div>
          ) : null}
        </form>
      </WaiverModalContent>
    </Modal>
  );
}
